import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { CriteriaProjectFreeActivityChangeForm } from '@/module/Laboratory/criteria/components/CriteriaProjectFreeActivityChangeForm';
import { CriteriaHeaderButtons } from '@/module/Laboratory/criteria/components/components/CriteriaHeaderButtons';
import { useSaveCriteriaData } from '@/module/Laboratory/criteria/components/hooks/useSaveCriteriaData';
import { VisibilityActivity, getCenterReportCriteriaDataOptions } from '@/module/ResearchCenter';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

import { Id } from '@feature/utils';

import { CriteriaFreeActivityCard } from './components/components/CriteriaFreeActivityCard';
import { CriteriaItemDeleteButton } from './components/components/CriteriaItemDeleteButton';

export interface CenterReportFreeActivityProps {
  labId: Id;
  criteriaCode: string;
}

const defaultActivityValues = { title: '', organizer: '', period: '' };

export function CenterReportFreeActivity({ labId, criteriaCode }: CenterReportFreeActivityProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    data: { completed, visibilityActivities },
  } = useSuspenseQuery(getCenterReportCriteriaDataOptions(labId, criteriaCode));
  const { onSubmit, isPending } = useSaveCriteriaData(labId, criteriaCode, close);

  const onChange = (text: VisibilityActivity) => onSubmit({ activityVisibility: text });
  const onRemove = (text: VisibilityActivity) => {
    if (text.id) {
      onSubmit({ toRemove: { id: text.id } });
    }
  };

  return (
    <div>
      <CriteriaHeaderButtons
        completed={completed}
        criteriaCode={criteriaCode}
        labId={labId}
        onAdd={open}
      />
      {opened && (
        <CriteriaProjectFreeActivityChangeForm
          defaultValues={defaultActivityValues}
          onChange={onChange}
          isSubmitting={isPending}
          onCancel={close}
        />
      )}
      <>{isPending && <PendingSpinner />}</>
      {!isPending && (
        <>
          {visibilityActivities.length > 0 && (
            <Stack>
              {visibilityActivities.map((freeActivity, index) => (
                <Flex
                  align="center"
                  key={freeActivity.id}
                >
                  <CriteriaFreeActivityCard
                    index={index}
                    freeActivity={freeActivity}
                    onChange={onChange}
                    isPending={isPending}
                    completed={completed}
                  />
                  {!completed && <CriteriaItemDeleteButton onRemove={() => onRemove(freeActivity)} />}
                </Flex>
              ))}
            </Stack>
          )}
          {visibilityActivities.length === 0 && <NoContentPlaceholder title={t('centerReport.noCriteriaContent')} />}
        </>
      )}
    </div>
  );
}
